/* eslint-disable max-depth */
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";
import { closeCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import url from "web/script/utils/url";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import withReduxProvider from "web/react/redux-provider";
import EmailCaptureForm from "web/react/components/email-capture-form/email-capture-form";
import styles from "./email-capture-overlay.css";

const CAPTURE_TYPE_SIGNUP_LEAD_SAVE = "signup_lead_save";
const CAPTURE_TYPE_SIGNUP_OOS_SAVE = "signup_oos_save";
const CAPTURE_TYPE_SIGNUP_PDP_SAVE = "signup_pdp_save";
const CAPTURE_TYPE_SIGNUP_FEED_SAVE = "signup_feed_save";
const CAPTURE_TYPE_SIGNUP_DU = "signup_du";
const CAPTURE_TYPE_SIGNUP_SEARCH = "signup_search";
const CAPTURE_TYPE_STOCK_ALERT = "stock_alert";
const CAPTURE_TYPE_STOCK_ALERT_LEAD_RETURN = "stock_alert_lead_return";

function createMarkup(value) {
    return { __html: value };
}

function mapStateToProps(state) {
    const {
        captureType,
        designerId,
        next,
        open,
        productId,
        searchGender,
        searchTerm,
    } = state.customerCaptureOverlayReducer;
    return {
        captureType,
        designerId,
        next,
        overlayOpen: open,
        productId,
        searchGender,
        searchTerm,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        closeCustomerCaptureOverlay: () => dispatch(closeCustomerCaptureOverlay()),
    };
}

export function EmailCaptureOverlay({
    captureType,
    closeCustomerCaptureOverlay,
    cookiePolicyLink,
    designerId,
    next,
    overlayOpen,
    privacyPolicyLink,
    productId,
    searchGender,
    searchTerm,
    termsAndConditionsLink,
    shortEUPolicy,
    signInLink,
}) {
    const country = environment.get("country");
    const emailLabelText = gettext("account.form.email_label");
    let submitText;
    let title;
    let type;

    switch (captureType) {
        case CAPTURE_TYPE_SIGNUP_PDP_SAVE:
        case CAPTURE_TYPE_SIGNUP_FEED_SAVE:
            submitText = gettext("account.register.return_lead_btn_txt_track_deals");
            title = gettext("saved_items.sign_up_save_item_title_option_b");
            type = "save_for_later";
            break;
        case CAPTURE_TYPE_SIGNUP_LEAD_SAVE:
            submitText = gettext("account.register.return_lead_btn_txt_get_deals");
            title = gettext("account.register.return_lead_title");
            type = "save_for_later_lead_return";
            break;
        case CAPTURE_TYPE_SIGNUP_SEARCH:
            submitText = gettext("account.register.return_lead_btn_follow_search");
            title = gettext("account.register.return_lead_title_follow_search", {
                search_terms: searchTerm,
            });
            type = "saved_search";
            break;
        case CAPTURE_TYPE_SIGNUP_OOS_SAVE:
        case CAPTURE_TYPE_STOCK_ALERT:
            submitText = gettext("general.alert_me");
            title = gettext("email_capture.stock_alert.title");
            type = "back_in_stock";
            break;
        case CAPTURE_TYPE_STOCK_ALERT_LEAD_RETURN:
            submitText = gettext("general.alert_me");
            title = gettext("email_capture.stock_alert.title_when_triggered_by_lead_link");
            type = "back_in_stock";
            break;
        case CAPTURE_TYPE_SIGNUP_DU:
            submitText = gettext("account.register.return_lead_btn_follow_products_designer");
            title = gettext("account.register.return_lead_title_follow_products_designer");
            type = "designer_update_lyst";
    }
    // Hooks
    const [emailAddress, setEmailAddress] = useState("");
    const [privacyPolicy, setPrivacyPolicy] = useState(false);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [formErrors, setFormErrors] = useState(undefined);

    let hidden;
    let visibilityChange;
    if (typeof document.hidden !== "undefined") {
        hidden = "hidden";
        visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
        hidden = "msHidden";
        visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
        hidden = "webkitHidden";
        visibilityChange = "webkitvisibilitychange";
    }

    function handleVisibilityChange() {
        if (!document[hidden]) {
            analytics.event(captureType, "view_form", "back_overlay");
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        }
    }

    useEffect(() => {
        return () => {
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        };
    });

    function triggerAfterOpen() {
        analytics.event(captureType, "open_signup_form", "open_overlay", true); // non interactive
        document.addEventListener(visibilityChange, handleVisibilityChange, false);
    }

    function triggerClose() {
        analytics.event(captureType, "close_signup_form", "exit_signup");
        document.removeEventListener(visibilityChange, handleVisibilityChange);
        closeCustomerCaptureOverlay();
    }

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }

    let appElement;
    if (document.body) {
        appElement = document.body.querySelector(".site-wrapper") || undefined;
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        setPrivacyPolicy(privacyPolicyChecked);
        setMarketingEmails(marketingEmailsChecked);
    }

    async function onSubmit(event) {
        event.preventDefault();
        analytics.event(captureType, "click_signup_button", "signup_button");

        const seenProducts = userProfiler.getSeenProducts();
        let productIds = [];
        if (seenProducts) {
            productIds = seenProducts.reduce((memo, product) => {
                memo.push(product.productId);
                return memo;
            }, []);
        }

        const body = {
            country,
            designer_id: designerId,
            email: emailAddress,
            invisible_pids: productIds.join(","),
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        const parsedURL = url.parse(window.location);
        // work out what the 'next' parameter should be
        parsedURL.searchParams.set("loggedin", "true");
        parsedURL.searchParams.set("area", "heart");

        const path = next || parsedURL.path;
        const params = url.toQueryString(parsedURL.searchParams);
        const nextParam = next || `${path}${params && `?${params}`}`;
        const searchTermParam = searchTerm || parsedURL.searchParams.get("q");
        const searchGenderParam =
            searchGender ||
            parsedURL.searchParams.get("gender") ||
            parsedURL.searchParams.get("meta_gender");

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: type,
                product_id: productId,
                designer_id: designerId,
                search_query: searchTermParam,
                search_gender: searchGenderParam,
                email_capture: "true",
                next: nextParam,
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors({ other: [gettext("account.login.with_magic_link.error_title")] });
            analytics.event(captureType, "error_signup", "server_error");
            return;
        }

        if (jsonData && jsonData.success) {
            closeCustomerCaptureOverlay();
            // this lets facebook campaign for signups know people registered
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.REGISTRATION_COMPLETE
            );
            window.location = jsonData.data ? jsonData.data.next : nextParam;
        } else {
            const { errors } = jsonData.data;
            Object.keys(errors).map((key) => {
                if (key === "marketing_emails") {
                    analytics.event(captureType, "missed_tickbox", "tickbox");
                }
            });
            setFormErrors(errors);
        }
    }

    function onSocialClick(authProvider) {
        event.preventDefault();

        // signup_facebook, signup_google
        analytics.event(captureType, "click_social_sign_up", `signup_${authProvider}`);

        if (privacyPolicy !== true || marketingEmails !== true) {
            setFormErrors({
                privacy_policy: [gettext("account.register.user_must_tick_both_boxes")],
            });
            return;
        }

        // start with the current URL (including all query parameters)
        const parsedURL = url.parse(window.location);

        // work out what the 'next' parameter should be
        const nextParam = next || url.unparse(parsedURL);

        // update the path to point to the correct auth provider
        parsedURL.path = `/social/login/${authProvider}/`;
        parsedURL.searchParams.set("next", nextParam);

        parsedURL.searchParams.set("isLogin", false);
        parsedURL.searchParams.set("marketingEmails", marketingEmails);

        parsedURL.searchParams.set("productId", productId);
        parsedURL.searchParams.set("designerId", designerId);
        parsedURL.searchParams.set("authType", type);
        parsedURL.searchParams.set("searchQuery", searchTerm);
        parsedURL.searchParams.set("searchGender", searchGender);

        // do the redirect
        window.location = url.unparse(parsedURL);
    }

    function getSignInLinkProps() {
        const parsedURL = url.parse(window.location);
        // work out what the 'next' parameter should be
        parsedURL.searchParams.set("loggedin", "true");
        parsedURL.searchParams.set("area", "heart");

        const path = next || parsedURL.path;
        const params = url.toQueryString(parsedURL.searchParams);
        const nextParam = next || `${path}${params && `?${params}`}`;
        const searchTermParam = searchTerm || parsedURL.searchParams.get("q");
        const searchGenderParam =
            searchGender ||
            parsedURL.searchParams.get("gender") ||
            parsedURL.searchParams.get("meta_gender");

        const queryParams = url
            .toQueryString({
                type: type,
                product_id: productId,
                designer_id: designerId,
                search_query: searchTermParam,
                search_gender: searchGenderParam,
                email_capture: "true",
                next: nextParam,
            })
            .toString();
        return signInLink + "?" + queryParams;
    }

    if (!overlayOpen) {
        return null;
    }

    return (
        <div id="customer-capture-overlay">
            <Modal
                appElement={appElement}
                isOpen={overlayOpen}
                onAfterOpen={triggerAfterOpen}
                onRequestClose={triggerClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <CloseButton className={styles.closeButton} onClick={triggerClose} />
                <div className={styles.captureForm}>
                    <h3 className={styles.header} dangerouslySetInnerHTML={createMarkup(title)} />
                    <EmailCaptureForm
                        captureType={captureType}
                        cookiePolicyLink={cookiePolicyLink}
                        emailAddress={emailAddress}
                        emailLabelText={emailLabelText}
                        formErrors={formErrors}
                        onEmailChange={changeEmail}
                        onLegalChange={handleLegalChange}
                        onSubmit={onSubmit}
                        privacyPolicyLink={privacyPolicyLink}
                        submitText={submitText}
                        termsAndConditionsLink={termsAndConditionsLink}
                        shortEUPolicy={shortEUPolicy}
                        onSocialClick={onSocialClick}
                        signInLinkProps={getSignInLinkProps()}
                    />
                </div>
            </Modal>
        </div>
    );
}

export default withReduxProvider(connect(mapStateToProps, mapDispatchToProps)(EmailCaptureOverlay));

EmailCaptureOverlay.propTypes = {
    captureType: PropTypes.oneOf([
        "signup_lead_save",
        "signup_pdp_save",
        "signup_feed_save",
        "signup_du",
        "signup_search",
        "stock_alert",
        "stock_alert_lead_return",
        "signup_oos_save",
    ]),
    closeCustomerCaptureOverlay: PropTypes.func,
    cookiePolicyLink: PropTypes.string,
    designerId: PropTypes.number,
    next: PropTypes.string,
    overlayOpen: PropTypes.bool,
    privacyPolicyLink: PropTypes.string,
    productId: PropTypes.string,
    searchGender: PropTypes.string,
    searchTerm: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortEUPolicy: PropTypes.bool,
    signInLink: PropTypes.string,
};
